import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import {
  EBAY_FEEDBACK_SOURCE_URL,
  EBAY_POSITIVE_COUNT,
  EBAY_REVIEWS_NOTE,
  ebayBuyerInitials,
  getFeaturedEbayReviews,
} from "../../lib/ebayReviews";

const SLIDE_MS = 6500;

/**
 * Featured positive eBay feedback carousel — curated substantive quotes only.
 * @param {{ mode?: 'full'|'teaser', className?: string, showSourceNote?: boolean, featuredSize?: boolean }} props
 */
export default function EbayReviewsCarousel({
  mode = "full",
  className = "",
  showSourceNote = true,
  featuredSize = false,
}) {
  const reviews = getFeaturedEbayReviews(mode === "teaser" ? 8 : 12);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = reviews.length;

  const goTo = (delta) => {
    if (count < 2) return;
    setIndex((i) => (i + delta + count) % count);
  };

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || paused || count < 2) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, SLIDE_MS);
    return () => window.clearInterval(id);
  }, [paused, count, index]);

  if (!count) return null;

  const current = reviews[index];
  const initials = ebayBuyerInitials(current.buyerLabel);

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div
        className="relative overflow-hidden rounded-[1.35rem] border border-white/12 bg-gradient-to-br from-white/[0.07] via-panel/90 to-void/80"
        role="region"
        aria-roledescription="carousel"
        aria-label="Featured positive eBay feedback"
        aria-live="polite"
      >
        <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden>
          <div className="absolute -top-24 right-0 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute bottom-0 -left-16 h-40 w-40 rounded-full bg-plum/15 blur-3xl" />
        </div>

        <div className={`relative ${featuredSize ? "px-5 py-7 sm:px-9 sm:py-9 md:px-11 md:py-10" : "px-5 py-6 sm:px-8 sm:py-8 md:px-10 md:py-9"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/12 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase text-gold">
              From eBay
              <span className="text-gold/70 normal-case tracking-normal">· marketplace</span>
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`${featuredSize ? "h-3.5 w-3.5" : "h-3 w-3"} fill-gold text-gold`}
                    strokeWidth={1.5}
                    aria-hidden
                  />
                ))}
              </div>
              <p className="text-xs text-sage m-0 tabular-nums">
                {index + 1} / {count}
              </p>
            </div>
          </div>

          <div
            key={current.id}
            className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-7 items-start mm-review-slide"
          >
            <div
              className={`flex shrink-0 items-center justify-center rounded-full border border-[rgba(248,241,223,0.18)] bg-void/50 font-sans font-semibold tracking-wide text-ivory ${
                featuredSize ? "h-14 w-14 text-base" : "h-12 w-12 text-sm"
              }`}
              aria-hidden
            >
              {initials}
            </div>

            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.14em] text-sage m-0 mb-1.5">
                Bought
              </p>
              <p className={`font-sans font-medium text-ivory leading-snug m-0 ${featuredSize ? "text-base md:text-lg" : "text-sm md:text-base"}`}>
                {current.productName}
              </p>

              <blockquote
                className={`mt-4 md:mt-5 m-0 font-sans leading-relaxed text-ivory ${
                  featuredSize
                    ? "text-base sm:text-lg md:text-[1.2rem] md:leading-relaxed"
                    : "text-sm md:text-[0.95rem] text-ivory/90"
                }`}
              >
                “{current.comment}”
              </blockquote>

              <footer className="mt-5 pt-4 border-t border-[rgba(248,241,223,0.12)] flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="text-sm text-ivory m-0">{current.buyerLabel}</p>
                {current.verifiedPurchase ? (
                  <p className="text-[11px] text-gold tracking-wide m-0">
                    Verified purchase
                  </p>
                ) : null}
                {current.dateLabel ? (
                  <p className="text-xs text-sage m-0">{current.dateLabel}</p>
                ) : null}
              </footer>
            </div>
          </div>

          {count > 1 ? (
            <div className="mt-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goTo(-1)}
                  aria-label="Previous eBay review"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/16 bg-void/40 text-ivory hover:border-gold/40 hover:text-gold transition"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => goTo(1)}
                  aria-label="Next eBay review"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/16 bg-void/40 text-ivory hover:border-gold/40 hover:text-gold transition"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </button>
              </div>

              <div
                className="flex flex-wrap justify-end gap-1.5"
                role="tablist"
                aria-label="Review slides"
              >
                {reviews.map((r, i) => (
                  <button
                    key={r.id}
                    type="button"
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Show review ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? "w-6 bg-gold" : "w-1.5 bg-white/25 hover:bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showSourceNote ? (
        <p className="mt-3 text-[11px] text-sage/75 leading-relaxed m-0 max-w-2xl">
          {EBAY_REVIEWS_NOTE} {EBAY_POSITIVE_COUNT} positive in the last 12 months.{" "}
          <a
            href={EBAY_FEEDBACK_SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-white/20 underline-offset-2 hover:text-goldhover"
          >
            See all feedback on eBay
          </a>
          .
        </p>
      ) : null}
    </div>
  );
}
